import { parseLogsMentionId } from "@/components/notebook/menus/agent-mention-search";
import type { IconName } from "@/components/icons/Icon";
import { formatIdentifier } from "@/components/query/sql/shared/sql-catalog-completion-engine";
import { QueryParam } from "@/utils/hooks/url/use-query-state";
import { Routes } from "@/utils/routes";

/**
 * Every kind of object a preview can describe. Adding a kind is one entry in
 * `ENTITY_KIND_DEFINITIONS` (label, icon, description, link, the pages that show one), plus an
 * optional live-details hook in `entity-preview-details`.
 */
export type EntityKind =
  // Notebook-local objects, which have no page of their own.
  | "cell"
  | "table"
  | "variable"
  | "input"
  // Environment objects.
  | "feature"
  | "resolver"
  | "query"
  | "dataset"
  | "model"
  | "model-deployment"
  | "function"
  | "scaling-group"
  | "sandbox"
  | "sql-catalog"
  | "sql-schema"
  | "sql-table"
  | "worksheet"
  | "notebook"
  | "online-query"
  | "offline-query"
  | "scheduled-run"
  | "dataframe-run"
  | "deployment"
  | "branch"
  | "chart"
  | "dashboard"
  | "monitor"
  | "incident"
  | "evaluation"
  | "logs"
  // Any other page, for surfaces that list visited pages.
  | "page";

export interface EntityRef {
  kind: EntityKind;
  id: string;
  name: string;
}

export interface EntityScope {
  projectId: string;
  environmentId: string;
}

/** A page that shows one entity: its route template below the environment root, as under
 *  `pages/`, and the route param that carries the entity's id. */
export interface EntityRoute {
  path: string;
  idParam: string;
}

export interface EntityKindDefinition {
  label: string;
  icon: IconName;
  /** What this kind is, for previews with no entity-specific description. */
  description: string;
  /** Text of the link out to the entity, when it has a page. */
  linkLabel: string | null;
  href: ((scope: EntityScope, id: string) => string) | null;
  routes: readonly EntityRoute[];
  /** Whether previews show the id under the name. Off for opaque ids (uuids) that tell a reader
   *  nothing, and for kinds whose id repeats what the preview already shows. */
  showsId: boolean;
}

/**
 * A preview query for a SQL table. The id is the dotted `catalog.schema.table` path; Chalk SQL
 * addresses it through the `datasources` namespace, with each segment quoted by the same rules
 * the SQL editor's completions use.
 */
export const sqlTablePreviewQuery = (tableId: string): string => {
  const tablePath = tableId
    .split(".")
    .filter((segment) => segment.length > 0)
    .map(formatIdentifier)
    .join(".");
  return `select * from datasources.${tablePath} limit 100`;
};

const sqlExplorer = (scope: EntityScope) =>
  Routes.project.environment.dataExplorerSql(scope.projectId, scope.environmentId);

const notebookLocal = (label: string, icon: IconName, description: string) =>
  ({ label, icon, description, linkLabel: null, href: null, routes: [], showsId: true }) as const;

export const ENTITY_KIND_DEFINITIONS: Readonly<Record<EntityKind, EntityKindDefinition>> = {
  cell: notebookLocal("Notebook cell", "notebook", "A cell in a notebook."),
  table: notebookLocal("Kernel table", "table", "A dataframe variable in a notebook kernel."),
  variable: notebookLocal("Kernel variable", "variable", "A variable in a notebook kernel."),
  input: notebookLocal("Template input", "variable", "An input a template takes when it runs."),
  feature: {
    label: "Feature",
    icon: "feature",
    description: "A feature from this environment's deployed graph.",
    linkLabel: "View feature",
    href: (scope, id) =>
      Routes.project.environment.feature(scope.projectId, scope.environmentId, id),
    routes: [
      { path: "features/[featureFqn]", idParam: "featureFqn" },
      { path: "deployments/[deploymentId]/features/[featureFqn]", idParam: "featureFqn" },
      {
        path: "branches/[branchName]/deployments/[deploymentId]/features/[featureFqn]",
        idParam: "featureFqn",
      },
    ],
    showsId: true,
  },
  resolver: {
    label: "Resolver",
    icon: "resolver",
    description: "A resolver from this environment's deployed graph.",
    linkLabel: "View resolver",
    href: (scope, id) =>
      Routes.project.environment.resolver(scope.projectId, scope.environmentId, id),
    routes: [
      { path: "resolvers/[resolverFqn]", idParam: "resolverFqn" },
      { path: "deployments/[deploymentId]/resolvers/[resolverFqn]", idParam: "resolverFqn" },
      {
        path: "branches/[branchName]/deployments/[deploymentId]/resolvers/[resolverFqn]",
        idParam: "resolverFqn",
      },
    ],
    showsId: true,
  },
  query: {
    label: "Named query",
    icon: "docs",
    description: "A named query defined in this environment's deployment.",
    linkLabel: "View named query",
    href: (scope, id) =>
      Routes.project.environment.namedQuery(scope.projectId, scope.environmentId, id),
    routes: [{ path: "queries/named/[queryName]", idParam: "queryName" }],
    showsId: true,
  },
  dataset: {
    label: "Dataset",
    icon: "database",
    description: "A dataset materialized by an offline query in this environment.",
    linkLabel: "View dataset",
    href: (scope, id) =>
      Routes.project.environment.dataset(scope.projectId, scope.environmentId, id),
    routes: [{ path: "datasets/[datasetId]", idParam: "datasetId" }],
    showsId: false,
  },
  model: {
    label: "Model",
    icon: "box",
    description: "A model from this environment's model registry.",
    linkLabel: "View model",
    href: (scope, id) => Routes.project.environment.model(scope.projectId, scope.environmentId, id),
    routes: [{ path: "models/[modelId]", idParam: "modelId" }],
    showsId: false,
  },
  "model-deployment": {
    label: "Model deployment",
    icon: "box",
    description: "A model serving deployment in this environment.",
    linkLabel: "View model deployment",
    href: (scope, id) =>
      Routes.project.environment.modelDeployment(scope.projectId, scope.environmentId, id),
    routes: [{ path: "model-deployments/[modelDeploymentId]", idParam: "modelDeploymentId" }],
    showsId: false,
  },
  function: {
    label: "Compute function",
    icon: "squareFunction",
    description: "A compute function registered in this environment.",
    linkLabel: "View compute function",
    href: (scope, id) =>
      Routes.project.environment.externalFunction(scope.projectId, scope.environmentId, id),
    routes: [
      { path: "external-functions/[externalFunctionName]", idParam: "externalFunctionName" },
    ],
    showsId: true,
  },
  "scaling-group": {
    label: "Scaling group",
    icon: "cpu",
    description: "A scaling group provisioning compute in this environment.",
    linkLabel: "View scaling group",
    href: (scope, id) =>
      Routes.project.environment.scalingGroup(scope.projectId, scope.environmentId, id),
    routes: [{ path: "scaling-groups/[scalingGroupIdOrName]", idParam: "scalingGroupIdOrName" }],
    showsId: true,
  },
  sandbox: {
    label: "Sandbox",
    icon: "box",
    description: "A sandbox container in this environment.",
    linkLabel: "View sandbox",
    href: (scope, id) =>
      Routes.project.environment.sandbox(scope.projectId, scope.environmentId, id),
    routes: [{ path: "sandboxes/[sandboxId]", idParam: "sandboxId" }],
    showsId: true,
  },
  // Catalogs and schemas have no page of their own; the SQL explorer is where you browse one.
  "sql-catalog": {
    label: "SQL catalog",
    icon: "database",
    description: "A catalog in this environment's SQL catalog.",
    linkLabel: "Open SQL explorer",
    href: (scope) => sqlExplorer(scope),
    routes: [],
    showsId: true,
  },
  "sql-schema": {
    label: "SQL schema",
    icon: "schema",
    description: "A schema in this environment's SQL catalog.",
    linkLabel: "Open SQL explorer",
    href: (scope) => sqlExplorer(scope),
    routes: [],
    showsId: true,
  },
  // A table has no page either, so its link opens the explorer on a preview of it.
  "sql-table": {
    label: "SQL table",
    icon: "table",
    description: "A table from this environment's SQL catalog.",
    linkLabel: "Open SQL explorer",
    href: (scope, id) =>
      `${sqlExplorer(scope)}?sql=${encodeURIComponent(sqlTablePreviewQuery(id))}`,
    routes: [],
    showsId: true,
  },
  worksheet: {
    label: "SQL worksheet",
    icon: "sqlFile",
    description: "A saved SQL worksheet in this environment.",
    linkLabel: "Open SQL explorer",
    href: (scope, id) =>
      `${sqlExplorer(scope)}?${QueryParam.WORKSHEET_ID}=${encodeURIComponent(id)}`,
    routes: [],
    showsId: false,
  },
  notebook: {
    label: "Notebook",
    icon: "notebook",
    description: "A notebook in this environment.",
    linkLabel: "Open notebook",
    href: (scope, id) =>
      Routes.project.environment.notebook(scope.projectId, scope.environmentId, id),
    routes: [{ path: "notebooks/[notebookId]", idParam: "notebookId" }],
    showsId: false,
  },
  "online-query": {
    label: "Online query",
    icon: "online",
    description: "An online query run in this environment.",
    linkLabel: "View query run",
    href: (scope, id) =>
      Routes.project.environment.queryRun(scope.projectId, scope.environmentId, id),
    routes: [{ path: "query-runs/[queryRunId]", idParam: "queryRunId" }],
    showsId: true,
  },
  "offline-query": {
    label: "Offline query",
    icon: "circlePile",
    description: "An offline query (batch job) in this environment.",
    linkLabel: "View offline query",
    href: (scope, id) =>
      Routes.project.environment.offlineQuery(scope.projectId, scope.environmentId, id),
    routes: [{ path: "offline-queries/[offlineQueryId]", idParam: "offlineQueryId" }],
    showsId: true,
  },
  "scheduled-run": {
    label: "Scheduled run",
    icon: "calendar",
    description: "A run of a scheduled resolver, query, or aggregation backfill.",
    linkLabel: "View run",
    href: (scope, id) => Routes.project.environment.run(scope.projectId, scope.environmentId, id),
    routes: [{ path: "runs/[runId]", idParam: "runId" }],
    showsId: true,
  },
  "dataframe-run": {
    label: "DataFrame run",
    icon: "run",
    description: "A DataFrame run in this environment.",
    linkLabel: "View DataFrame run",
    href: (scope, id) =>
      Routes.project.environment.dataframeRun(scope.projectId, scope.environmentId, id),
    routes: [{ path: "dataframe-runs/[dataframeRunId]", idParam: "dataframeRunId" }],
    showsId: true,
  },
  deployment: {
    label: "Deployment",
    icon: "rocket",
    description: "A deployment of this environment.",
    linkLabel: "View deployment",
    href: (scope, id) =>
      Routes.project.environment.deployment(scope.projectId, scope.environmentId, id),
    routes: [{ path: "deployments/[deploymentId]", idParam: "deploymentId" }],
    showsId: true,
  },
  branch: {
    label: "Branch",
    icon: "gitBranch",
    description: "A branch deployment of this environment.",
    linkLabel: "View branch",
    href: (scope, id) =>
      Routes.project.environment.branch(scope.projectId, scope.environmentId, id),
    routes: [{ path: "branches/[branchName]", idParam: "branchName" }],
    showsId: true,
  },
  chart: {
    label: "Metric chart",
    icon: "chart",
    description: "A saved metric chart in this environment.",
    linkLabel: "View chart",
    href: (scope, id) =>
      Routes.project.environment.metricChart(scope.projectId, scope.environmentId, id),
    routes: [{ path: "metrics/chart/[chartId]", idParam: "chartId" }],
    showsId: true,
  },
  dashboard: {
    label: "Dashboard",
    icon: "grid",
    description: "A saved dashboard in this environment.",
    linkLabel: "View dashboard",
    href: (scope, id) =>
      Routes.project.environment.dashboardDetail(scope.projectId, scope.environmentId, id),
    routes: [{ path: "dashboards/[dashboardId]", idParam: "dashboardId" }],
    showsId: true,
  },
  monitor: {
    label: "Monitor",
    icon: "notifications",
    description: "A monitor in this environment.",
    linkLabel: "View monitor",
    href: (scope, id) =>
      Routes.project.environment.monitor(scope.projectId, scope.environmentId, id),
    routes: [{ path: "monitors/[monitorId]", idParam: "monitorId" }],
    showsId: true,
  },
  incident: {
    label: "Incident",
    icon: "alertOctagon",
    description: "An incident raised by a monitor in this environment.",
    linkLabel: "View incident",
    href: (scope, id) =>
      Routes.project.environment.incident(scope.projectId, scope.environmentId, id),
    routes: [{ path: "incidents/[incidentId]", idParam: "incidentId" }],
    showsId: true,
  },
  evaluation: {
    label: "Evaluation",
    icon: "bullseye",
    description: "An evaluation of an agent function over a dataset.",
    linkLabel: "View evaluation",
    href: (scope, id) =>
      Routes.project.environment.evaluation(scope.projectId, scope.environmentId, id),
    routes: [{ path: "evaluations/[evaluationId]", idParam: "evaluationId" }],
    showsId: false,
  },
  // Mention IDs serialize the search separately from its destination URL.
  logs: {
    label: "Log search",
    icon: "horizontalListQuery",
    description: "A log search over this environment.",
    linkLabel: "Open in logs",
    href: (scope, id) =>
      Routes.project.environment.logs.index(
        scope.projectId,
        scope.environmentId,
        undefined,
        parseLogsMentionId(id).query,
      ),
    routes: [],
    showsId: false,
  },
  page: {
    label: "Page",
    icon: "file",
    description: "A page in this environment.",
    linkLabel: null,
    href: null,
    routes: [],
    showsId: false,
  },
};

export const getEntityKindDefinition = (kind: EntityKind): EntityKindDefinition =>
  ENTITY_KIND_DEFINITIONS[kind];

/** The entity's own page, or null when its kind has none or the scope is unknown. */
export const getEntityHref = (
  entity: Pick<EntityRef, "kind" | "id">,
  scope: Partial<EntityScope>,
): string | null => {
  const { href } = ENTITY_KIND_DEFINITIONS[entity.kind];
  if (href == null || scope.projectId == null || scope.environmentId == null) return null;
  return href({ projectId: scope.projectId, environmentId: scope.environmentId }, entity.id);
};

const ENVIRONMENT_ROUTE_PREFIX = "/projects/[projectId]/environments/[environmentId]/";

const ENTITY_ROUTE_BY_PATHNAME: ReadonlyMap<string, { kind: EntityKind; idParam: string }> =
  new Map(
    (Object.entries(ENTITY_KIND_DEFINITIONS) as [EntityKind, EntityKindDefinition][]).flatMap(
      ([kind, definition]) =>
        definition.routes.map(
          (route) =>
            [`${ENVIRONMENT_ROUTE_PREFIX}${route.path}`, { kind, idParam: route.idParam }] as const,
        ),
    ),
  );

/** The entity a page shows, from its Next.js route template and query, if any kind claims it. */
export const matchEntityRoute = (
  pathname: string,
  query: Readonly<Record<string, string | string[] | undefined>>,
): Pick<EntityRef, "kind" | "id"> | undefined => {
  const match = ENTITY_ROUTE_BY_PATHNAME.get(pathname);
  if (match == null) return undefined;

  const value = query[match.idParam];
  const id = Array.isArray(value) ? value[0] : value;
  return id == null || id.length === 0 ? undefined : { kind: match.kind, id };
};

export const isEntityKind = (value: unknown): value is EntityKind =>
  typeof value === "string" && Object.prototype.hasOwnProperty.call(ENTITY_KIND_DEFINITIONS, value);
